import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { useEvent } from "../../hooks";
import { formatEventDate, formatTimeRange } from "./Events";

/**
 * mapsHref — Google Maps search link for an address string.
 * @param {string} [address]
 * @returns {string|null}
 */
function mapsHref(address) {
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/**
 * EventDetail — Single-event detail page (F-013, T-002).
 *
 * Reads GET /api/events/{id} and renders the full event with date/time,
 * location + map link, hosting vehicle, and description. Styled per BLU-006.
 */
export default function EventDetail() {
  const { id } = useParams();
  const { event, isLoading, error } = useEvent(id);

  return (
    <main data-testid="event-detail-page" className="min-h-screen bg-cream text-ink font-body">
      <section className="max-w-3xl mx-auto px-4 py-16">
        <Link
          to="/events"
          className="font-heading tracking-widest text-teal text-sm hover:text-teal-deep transition-colors"
          data-testid="back-to-events"
        >
          ← ALL EVENTS
        </Link>

        {isLoading && (
          <p className="font-body text-ink/60 italic mt-6" data-testid="event-loading">
            Loading event…
          </p>
        )}
        {error && (
          <p className="font-body text-rust mt-6" data-testid="event-error">
            We couldn't load this event. It may have ended or been removed.
          </p>
        )}

        {!isLoading && !error && event && (
          <article className="mt-4" data-testid={`event-detail-${event.id}`}>
            <p className="font-heading tracking-[0.3em] text-teal-deep text-sm">
              {formatEventDate(event.eventDate)} · {moment(event.eventDate, "YYYY-MM-DD").format("YYYY")}
            </p>
            <h1 className="font-display text-5xl text-charcoal uppercase mt-1">{event.title}</h1>

            <div className="bg-white border-2 border-charcoal rounded-2xl p-6 shadow-[4px_4px_0_rgba(43,39,36,0.12)] mt-6 space-y-3">
              <p className="font-body text-ink">
                <span className="font-heading tracking-widest text-teal-deep text-sm block">WHEN</span>
                {formatEventDate(event.eventDate)} · {formatTimeRange(event.startTime, event.endTime)}
              </p>
              <p className="font-body text-ink">
                <span className="font-heading tracking-widest text-teal-deep text-sm block">WHERE</span>
                {event.locationAddress}
                {mapsHref(event.locationAddress) && (
                  <>
                    {" "}
                    <a
                      href={mapsHref(event.locationAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal underline hover:text-teal-deep"
                      data-testid="event-map-link"
                    >
                      (map)
                    </a>
                  </>
                )}
              </p>
              {event.vehicleName && (
                <p className="font-body text-ink">
                  <span className="font-heading tracking-widest text-teal-deep text-sm block">RIG</span>
                  {event.vehicleName}
                </p>
              )}
            </div>

            {event.description && (
              <p className="font-body text-ink/80 leading-relaxed mt-6 whitespace-pre-line">
                {event.description}
              </p>
            )}
          </article>
        )}
      </section>
    </main>
  );
}
